/**
 * s237 Contacts PR F (D-237.68-5/6, D-237.71, D-237.72) — the two files Contacts' Download makes, from the SAME entries
 * the page shows: a spreadsheet (CSV, opens in Excel, Numbers or Google Sheets) and a contacts file (vCard 3.0, one .vcf
 * holding many cards, for Outlook, Google or iCloud contacts, or a phone). The page builds both in the browser; the
 * texted link (server/contact-files.ts) builds the .vcf on the server through the same function, so the two never drift.
 * Pure.
 */
import { formatAddressSummary } from "./address";

/** The parts of a Contacts entry the files read (`ContactsPageEntry`, server/across/contacts-page.ts). */
export interface ExportContact {
  kind: "person" | "organisation";
  name: string;
  firstName: string | null;
  lastName: string | null;
  firm: string | null;
  phone: string | null;
  email: string | null;
  address: { street: string | null; street2: string | null; city: string | null; state: string | null; postal: string | null; country: string | null } | null;
  roles: ReadonlyArray<{ words: string }>;
}

/** The spreadsheet's columns, in order (the prompt's list; "What they are to you" is the page's own heading). */
export const CSV_HEADINGS = ["Name", "Firm", "What they are to you", "Email", "Phone", "Address"] as const;

const addressOf = (c: ExportContact): string => {
  const a = c.address;
  if (!a) return "";
  return formatAddressSummary(JSON.stringify({ street: a.street ?? "", street2: a.street2 ?? "", city: a.city ?? "", state: a.state ?? "", postal: a.postal ?? "", country: a.country ?? "" }));
};
const rolesOf = (c: ExportContact) => c.roles.map((r) => r.words).filter(Boolean).join(", ");

/**
 * One spreadsheet cell. Quoted when it holds a comma, quote or line break. A cell a spreadsheet would run as a formula
 * (starts with =, @, or a + or - that is not a phone number's) gets a leading apostrophe, so it shows as typed.
 */
export function csvCell(v: string): string {
  let s = v ?? "";
  if (/^[=@\t\r]/.test(s) || /^[+-](?![\d\s(])/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The spreadsheet: a heading row, then one row per contact. CRLF line ends and a byte-order mark, so Excel reads accents. */
export function contactsCsv(list: readonly ExportContact[]): string {
  const rows = [CSV_HEADINGS as readonly string[]].concat(
    list.map((c) => [c.name, c.kind === "organisation" ? "" : c.firm ?? "", rolesOf(c), c.email ?? "", c.phone ?? "", addressOf(c)]),
  );
  return "\uFEFF" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

/** vCard 3.0 text escaping (RFC 2426 §4): backslash, comma, semicolon, line break. */
export function vcardText(v: string | null | undefined): string {
  return (v ?? "").replace(/\\/g, "\\\\").replace(/\r\n|\r|\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

/** Fold a content line at 75 characters (RFC 2425 §5.8.1: CRLF then one space). */
function fold(line: string): string {
  const chars = Array.from(line);
  if (chars.length <= 75) return line;
  const parts: string[] = [];
  for (let i = 0; i < chars.length; i += i === 0 ? 75 : 74) parts.push(chars.slice(i, i + (i === 0 ? 75 : 74)).join(""));
  return parts.join("\r\n ");
}

/** One contact as a vCard 3.0 card. A company is its own card: its name is the ORG (and FN), with no person's name. */
export function contactVcard(c: ExportContact): string {
  const company = c.kind === "organisation";
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  if (company) {
    lines.push("N:;;;;", `FN:${vcardText(c.name)}`, `ORG:${vcardText(c.name)}`, "X-ABShowAs:COMPANY");
  } else {
    const first = c.firstName ?? (c.lastName ? "" : c.name);
    lines.push(`N:${vcardText(c.lastName)};${vcardText(first)};;;`, `FN:${vcardText(c.name)}`);
    if (c.firm) lines.push(`ORG:${vcardText(c.firm)}`);
  }
  if (c.phone) lines.push(`TEL;TYPE=${company ? "WORK" : "CELL"},VOICE:${vcardText(c.phone)}`);
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET:${vcardText(c.email)}`);
  const a = c.address;
  if (a && [a.street, a.street2, a.city, a.state, a.postal, a.country].some((v) => (v ?? "").trim())) {
    // ADR: PO box ; extended (unit) ; street ; city ; region ; postal code ; country.
    lines.push(`ADR;TYPE=${company ? "WORK" : "HOME"}:;${vcardText(a.street2)};${vcardText(a.street)};${vcardText(a.city)};${vcardText(a.state)};${vcardText(a.postal)};${vcardText(a.country)}`);
  }
  const roles = rolesOf(c);
  if (roles) lines.push(`NOTE:${vcardText(roles)}`);
  lines.push("END:VCARD");
  return lines.map(fold).join("\r\n");
}

/** The contacts file: every card, one after another, in one .vcf. */
export function contactsVcf(list: readonly ExportContact[]): string {
  return list.map(contactVcard).join("\r\n") + "\r\n";
}

/** The two files' names. */
export const CONTACTS_FILE_NAMES = { csv: "contacts.csv", vcf: "contacts.vcf" } as const;
